import BaseStore from "core/lib/baseStore";

import isObjectEmpty from "core/helpers/common/isObjectEmpty";
import getTimeZoneOffset from "core/helpers/date/getTimeZoneOffset";

import systemLanguageService from "core/services/apiServices/systemLanguageService";
import accountService from "core/services/apiServices/accountService";
import browserSessionStorage from "core/services/storageService/sessionStorage";
import dateService from "core/services/dateService";

import globalInitialState from "./initialState";

import { authStore } from "../authStore";
import { USER_ROLE } from "core/constants/auth";
import { USER_ROLE_SPECIFIC_PERMISSIONS } from "core/constants/permission";
import timeZones from "core/systemData/timezones";

import binaryToFlags from "core/helpers/common/binaryToFlags";


class GlobalStore extends BaseStore {
    constructor() {
		super(globalInitialState);
	}

    setMainInfoLoading(isLoading){
        this.state.isMainInfoLoading = isLoading;
    }

    setLoading(isLoading){
        this.state.isLoading = isLoading; 
    }

    getUserInfo(){
        return accountService.getUserInfo()
            .then(value => {

                const info = {...value };

                const { allRoles, role } = authStore.getState().user;

                const userRoles = allRoles ? binaryToFlags(Object.values(USER_ROLE), allRoles) : [role];

                info.permissions = userRoles
                    .map(role => role === USER_ROLE.ADMIN ? value.permissions ?? {} : USER_ROLE_SPECIFIC_PERMISSIONS[role])
                    .reduce((accumlator, current) => {
                        Object.keys(current).forEach(item => {
                            if(!accumlator[item]){
                                accumlator[item] = current[item]
                            } else {
                                accumlator[item] = [...new Set([...accumlator[item], ...current[item]])]
                            }
                        })
                        return accumlator;
                    }, {})

                this.state.userInfo = info;

                this.state.companies = value.companies.map(c => ({
                    id: c.id,
                    name: c.name,
                    type: c.type,
                    projects: c.projects ?? []
                }))

                const formatSetting = info.formatSetting;

                const timeZoneId = formatSetting?.timeZoneId;

                const timeZoneConfig = timeZones.find(config => config.id === timeZoneId);

                if (timeZoneConfig) {
                    let timeZoneOffset = getTimeZoneOffset(timeZoneId, timeZones);
                    const daylightSaving = formatSetting?.daylightSaving;

                    const dayLightRangeStart = timeZoneConfig?.dstStart;
                    const dayLightRangeEnd = timeZoneConfig?.dstEnd;

                    if (daylightSaving && dayLightRangeStart && dayLightRangeEnd) {
                        const nowUtc = new Date();
                        const isInDaylightRange = dateService.isInRange(nowUtc, timeZoneConfig.dstStart.$date, timeZoneConfig.dstEnd.$date);

                        if (isInDaylightRange) {
                            timeZoneOffset = timeZoneOffset + 1;
                        }
                    }

                    dateService.setDateFormat(formatSetting?.dateFormatting);
                    dateService.setTimeFormat(formatSetting?.timeFormatting);
                    dateService.setTimeZone(timeZoneOffset);
                    dateService.setTimeZoneId(timeZoneId);
                    dateService.setDaylightSaving(daylightSaving);
                } else {
                    dateService.setTimeZoneId(null);
                }
            })
    }

    updateUserInfo(info){
        const currentValue = this.state.userInfo;
        this.state.userInfo = {
            ...currentValue,
            ...info
        }
    }

    changeCurrency(currency){
        browserSessionStorage.set("currency", currency);
        this.state.currency = currency;
    }

    getCurrencies(){
        return accountService.getCurrencies()
            .then(value => {
                const selectedCurrency = this.state.currency;
                const selectedCurrencyExist = value.some(c => c.currencyCode === selectedCurrency)
                if(!selectedCurrency || !selectedCurrencyExist){
                    const defaultCurrency = value.find(c => c.currency?.isDefault) ?? value[0];
                    this.changeCurrency(defaultCurrency.currencyCode)
                }

                this.state.currencies = value;
            })
    }

    getSystemLanguages(){
        const storageLanguages = browserSessionStorage.get("languages") ?? {};
        const isSystemLanguagesLoaded = !isObjectEmpty(storageLanguages)

        if(isSystemLanguagesLoaded){
            return Promise.resolve(storageLanguages);
        }

        return systemLanguageService.getSystemLanguages()
            .then(({ data: { value } }) => {
                this.state.languages = value;
                browserSessionStorage.set("languages", value);
                return value;
            })
            .catch(() => {
                this.state.languages = {};
                browserSessionStorage.set("languages", {});
            });
    }

    updateUserAvatar(avatarId){
        this.state.userInfo.avatarId = avatarId
    }

    updateRegistrationType(companyId, projectId, registrationType){
        this.state.companies = this.state.companies.map(c => c.id !== companyId ? c : ({
            ...c,
            projects: c.projects.map(p => p.id !== projectId ? p : ({
                ...p,
                registrationType
            }))
        }))
    }

    updateRegulatorType(companyId, projectId, regulatorType){
        this.state.companies = this.state.companies.map(c => c.id !== companyId ? c : ({
            ...c,
            projects: c.projects.map(p => p.id !== projectId ? p : ({
                ...p,
                regulatorType
            }))
        }))
    }
}

export default GlobalStore;